/**
 * @fileoverview My Page filter option lists.
 * @description The closed sets of stage codes, display statuses and priorities the My Page table's
 * filters offer, in the order the backend documents them. The stage labels are the backend's own
 * (the rows carry their label too), so the only text translated is the display status and the
 * priority.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import type {
  MyPageDisplayStatus,
  MyPagePriority,
  MyPageStageCode,
} from '@/features/sales/types/my-page.types'

/** @description Every stage a target's bank can be at, in pipeline order, with its label. */
export const MY_PAGE_STAGE_OPTIONS: readonly { code: MyPageStageCode; label: string }[] = [
  { code: 'NEW', label: 'New' },
  { code: 'CONTACTED', label: 'Contacted' },
  { code: 'DEMO_REQUESTED', label: 'Demo Requested' },
  { code: 'PROPOSAL_SENT', label: 'Proposal Sent' },
  { code: 'UNDER_NEGOTIATION', label: 'Under Negotiation' },
  { code: 'SUBSCRIPTION_REQUESTED', label: 'Subscription Requested' },
  { code: 'LIVE', label: 'Live' },
  { code: 'LOST', label: 'Lost' },
  { code: 'ON_HOLD', label: 'On Hold' },
  { code: 'REJECTED', label: 'Rejected' },
]

/** @description The display statuses the table can filter by (completed targets are not listed). */
export const MY_PAGE_STATUS_OPTIONS: readonly Exclude<MyPageDisplayStatus, 'COMPLETED'>[] = [
  'ON_TRACK',
  'AT_RISK',
  'OVERDUE',
  'AWAITING_PAYMENT',
  'CLOSED',
]

/** @description The priorities, highest first. */
export const MY_PAGE_PRIORITY_OPTIONS: readonly MyPagePriority[] = ['HIGH', 'MEDIUM', 'LOW']

/** @description The stages a target cannot be moved to without saying why (1-500 characters). */
export const MY_PAGE_REASON_STAGES: readonly MyPageStageCode[] = ['LOST', 'ON_HOLD', 'REJECTED']

/** @description The longest reason the backend accepts on a stage move, reopening or reassignment. */
export const MY_PAGE_REASON_MAX_LENGTH = 500
